import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import { v4 } from "uuid";

import { CodeInput } from "src/base-components/CodeInput/CodeInput";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Icon } from "src/base-components/Icon";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import {
  AbsoluteTimePointTypeEnum,
  ContainsConditionInputTypeEnum,
  EqualsConditionInputTypeEnum,
  GreaterThanConditionInputTypeEnum,
  GreaterThanOrEqualsConditionInputTypeEnum,
  IsNullConditionInputTypeEnum,
  LessThanConditionInputTypeEnum,
  LessThanOrEqualsConditionInputTypeEnum,
  EntityPropertyRefTypeEnum,
  FeatureRefTypeEnum,
  RelativeTimePointTypeEnum,
  LiteralTypeEnum,
  IsInConditionInputTypeEnum,
  NotConditionInputTypeEnum,
  TimeUnit,
} from "src/clients/jobs";
import { Operator } from "src/constants/operators";
import { EntityFieldType } from "src/entities/queries";
import { DateDatetimeSelector } from "src/jobs/common/entitySource/DateDatetimeSelector";
import {
  EntityFieldOption,
  EntityFieldsDropdown,
} from "src/jobs/common/entitySource/EntityFieldsDropdown";
import { JUNCTION_OPTIONS } from "src/jobs/common/entitySource/FilterConditionsAccordion";
import {
  Condition,
  FieldErrors,
  FormData,
  Lhs,
  operatorsList,
  Rhs,
  UnaryCondition,
} from "src/jobs/common/entitySource/types";
import {
  buildChangeset,
  doesNotContain,
  getAvailableOperators,
  getAvailableOperatorsOptions,
  isDateOrDatetimeField,
  isNotIn,
  isUnaryOperators,
  notEquals,
  notIsNull,
  OperatorsWithNot,
  translateToOperator,
} from "src/jobs/common/entitySource/utils";
import { assertUnreachable } from "src/utils/typeUtils";

type FilterConditionProps = {
  dataLoc: string;
  options: EntityFieldOption[];
  fieldTypes: Record<string, EntityFieldType>;
  onRemove?: () => void;
  mode: "junction" | "where";
  disabledJunction?: boolean;
  index: number;
  groupIndex: number;
  fieldErrors: FieldErrors;
};

type RetType = {
  operator: OperatorsWithNot;
  field: string;
  fieldType: EntityFieldType;
  rhs: Rhs | null;
  rhsValue: string | number;
  lhsId: string;
};

const extractDetails = (
  fieldValue: Condition | UnaryCondition | null,
  fieldTypes: Record<string, EntityFieldType>,
): RetType => {
  if (!fieldValue) {
    return {
      operator: EqualsConditionInputTypeEnum.EQ,
      field: "",
      fieldType: PropertyDefinitionOutputTypeEnum.STRING,
      rhs: null,
      rhsValue: "",
      lhsId: "",
    };
  }

  const field = (() => {
    if ("value" in fieldValue) {
      const valueObj = fieldValue.value;
      if (!valueObj) {
        return "";
      }

      if (valueObj.type === FeatureRefTypeEnum.FEATURE_REF) {
        return valueObj.feature_key || "";
      }
      if (valueObj.type === EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF) {
        return valueObj.property_name || "";
      }

      return "";
    }

    if (!("lhs" in fieldValue)) {
      return "";
    }

    const lhsObj = fieldValue.lhs;
    if (!lhsObj) {
      return "";
    }

    if (lhsObj.type === FeatureRefTypeEnum.FEATURE_REF) {
      return lhsObj.feature_key || "";
    } else if (lhsObj.type === EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF) {
      return lhsObj.property_name || "";
    }

    return "";
  })();

  const rhs = "rhs" in fieldValue ? fieldValue.rhs : null;

  const lhsId = (() => {
    if ("lhs" in fieldValue) {
      return fieldValue.lhs.id;
    }
    return "";
  })();

  const rhsValue = (() => {
    // UnaryConditions don't have rhs
    if ("value" in fieldValue) {
      return "";
    }

    if (rhs) {
      if ("value" in rhs) {
        return rhs.value;
      }

      if ("timestamp_seconds" in rhs) {
        return rhs.timestamp_seconds;
      }

      if ("offset" in rhs) {
        return rhs.offset;
      }
    }

    return "";
  })();

  switch (fieldValue.type) {
    case ContainsConditionInputTypeEnum.CONTAINS:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case IsInConditionInputTypeEnum.IS_IN:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case EqualsConditionInputTypeEnum.EQ:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case GreaterThanConditionInputTypeEnum.GT:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case GreaterThanOrEqualsConditionInputTypeEnum.GTE:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case IsNullConditionInputTypeEnum.IS_NULL:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs: null,
        rhsValue: "",
        lhsId,
      };

    case LessThanConditionInputTypeEnum.LT:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case LessThanOrEqualsConditionInputTypeEnum.LTE:
      return {
        operator: fieldValue.type,
        field,
        fieldType: fieldTypes[field],
        rhs,
        rhsValue,
        lhsId,
      };

    case NotConditionInputTypeEnum.NOT: {
      const {
        lhsId,
        operator,
        fieldType,
        rhs,
        field,
        rhsValue: rhsValueInner,
      } = extractDetails(fieldValue.condition, fieldTypes);
      const joinedOperator = (() => {
        switch (operator) {
          case ContainsConditionInputTypeEnum.CONTAINS:
            return doesNotContain;
          case EqualsConditionInputTypeEnum.EQ:
            return notEquals;
          case IsNullConditionInputTypeEnum.IS_NULL:
            return notIsNull;
          case IsInConditionInputTypeEnum.IS_IN:
            return isNotIn;
          default:
            throw new Error("not implemented");
        }
      })();
      return {
        lhsId,
        operator: joinedOperator,
        field,
        fieldType: fieldType,
        rhs: rhs,
        rhsValue: rhsValueInner,
      };
    }
    default:
      assertUnreachable(fieldValue);
      return null as unknown as RetType;
  }
};

export const FilterCondition: React.FC<FilterConditionProps> = ({
  dataLoc,
  options,
  mode,
  disabledJunction,
  index,
  groupIndex,
  fieldTypes,
  onRemove,
  fieldErrors,
}) => {
  const { control } = useFormContext<FormData>();

  return (
    <div
      className="flex items-center gap-x-2"
      data-loc={`container-${dataLoc}`}
    >
      <div className="basis-[72px]">
        {mode === "junction" && (
          <Controller
            control={control}
            name={`filter_condition.conditions.${groupIndex}.type`}
            render={({ field }) => (
              <FixedSimpleDropdown
                dataLoc={`${dataLoc}-junction-dropdown`}
                disabled={disabledJunction}
                elements={JUNCTION_OPTIONS}
                placeholder="and"
                selectedKey={field.value}
                onSelect={field.onChange}
              />
            )}
          />
        )}
        {mode === "where" && (
          <p className="text-gray-700 font-inter-medium-12px">Where</p>
        )}
      </div>
      <div className="w-1/3 flex-1">
        <Controller
          control={control}
          name={`filter_condition.conditions.${groupIndex}.conditions.${index}`}
          render={({ field: { value: fieldValue, onChange } }) => {
            if (!fieldValue || !operatorsList.includes(fieldValue.type)) {
              return <></>;
            }

            const { lhsId, operator, fieldType, field, rhsValue } =
              extractDetails(fieldValue, fieldTypes);

            return (
              <EntityFieldsDropdown
                dataLoc={`${dataLoc}-field`}
                error={fieldErrors?.[lhsId]}
                options={options}
                value={field}
                onChange={(selectedKey) => {
                  const newField = options.find(
                    (option) => option.key === selectedKey,
                  );
                  const availableOperators = getAvailableOperators(
                    newField?.fieldType ?? null,
                  );

                  const newOperator = availableOperators.includes(operator)
                    ? operator
                    : (availableOperators[0] ?? "eq");

                  const lhs: Lhs = (() => {
                    if (newField?.sectionKey === "features") {
                      return {
                        id: v4(),
                        type: FeatureRefTypeEnum.FEATURE_REF,
                        feature_key: selectedKey,
                        fieldType:
                          newField?.fieldType ??
                          PropertyDefinitionOutputTypeEnum.STRING,
                      };
                    }
                    return {
                      id: v4(),
                      type: EntityPropertyRefTypeEnum.ENTITY_PROPERTY_REF,
                      property_name: selectedKey,
                      fieldType:
                        newField?.fieldType ??
                        PropertyDefinitionOutputTypeEnum.STRING,
                    };
                  })();

                  const newRhs = (() => {
                    if (isDateOrDatetimeField(newField?.fieldType ?? null)) {
                      return {
                        id: v4(),
                        type: AbsoluteTimePointTypeEnum.TIME_POINT_ABSOLUTE,
                        timestamp_seconds: 0,
                      };
                    }

                    return {
                      id: v4(),
                      type: LiteralTypeEnum.LITERAL,
                      value: isDateOrDatetimeField(fieldType)
                        ? ""
                        : String(rhsValue),
                    };
                  })();

                  const newState = buildChangeset(
                    fieldValue,
                    translateToOperator(newOperator),
                    newRhs,
                    lhs,
                  );
                  onChange(newState);
                }}
              />
            );
          }}
        />
      </div>
      <div className="w-1/3 flex-1">
        <Controller
          control={control}
          name={`filter_condition.conditions.${groupIndex}.conditions.${index}`}
          render={({ field: { value: fieldValue, onChange } }) => {
            const { operator, fieldType, rhs, rhsValue } = extractDetails(
              fieldValue,
              fieldTypes,
            );

            if (isDateOrDatetimeField(fieldType)) {
              const rhsValueNumber = (() => {
                if (typeof rhsValue === "number") {
                  return rhsValue;
                }
                const parsedValue = parseInt(rhsValue, 10);
                return Number.isNaN(parsedValue) ? null : parsedValue;
              })();

              const rhsUnit = (() => {
                if (rhs && "unit" in rhs) {
                  return rhs.unit ?? TimeUnit.DAYS;
                }
                return TimeUnit.DAYS;
              })();

              const dateValue = (() => {
                if (rhsValueNumber) {
                  return rhs?.type ===
                    AbsoluteTimePointTypeEnum.TIME_POINT_ABSOLUTE
                    ? { timestamp_seconds: rhsValueNumber }
                    : { offset: rhsValueNumber, unit: rhsUnit };
                }

                return null;
              })();

              return (
                <DateDatetimeSelector
                  dataLoc={`${dataLoc}-datetime-selector`}
                  error={fieldErrors?.[rhs?.id ?? ""]}
                  fieldType={fieldType}
                  operatorDataLoc={`${dataLoc}-operator-dropdown`}
                  value={{
                    value: dateValue,
                    operator,
                  }}
                  onChange={({ operator, value: changedValue }) => {
                    const newRhs =
                      changedValue && "timestamp_seconds" in changedValue
                        ? {
                            id: v4(),
                            type: AbsoluteTimePointTypeEnum.TIME_POINT_ABSOLUTE,
                            timestamp_seconds: changedValue.timestamp_seconds,
                          }
                        : {
                            id: v4(),
                            type: RelativeTimePointTypeEnum.TIME_POINT_RELATIVE,
                            offset: changedValue?.offset ?? -1,
                            unit: changedValue?.unit ?? TimeUnit.DAYS,
                          };

                    onChange(
                      buildChangeset(
                        fieldValue,
                        translateToOperator(operator),
                        newRhs,
                      ),
                    );
                  }}
                />
              );
            }

            return (
              <CodeInput
                dataLoc={`${dataLoc}-code-input`}
                error={fieldErrors?.[rhs?.id ?? ""]}
                readOnly={isUnaryOperators(operator)}
                value={String(rhsValue as string)}
                onChange={(value) => {
                  if (rhs) {
                    onChange(
                      buildChangeset(
                        fieldValue,
                        translateToOperator(operator),
                        {
                          id: v4(),
                          type: LiteralTypeEnum.LITERAL,
                          value,
                        },
                      ),
                    );
                  }
                }}
              >
                <OperatorDropdown
                  dataLoc={`${dataLoc}-operator-dropdown`}
                  options={getAvailableOperatorsOptions(fieldType)}
                  value={translateToOperator(operator)}
                  onChange={(operatorArg) => {
                    onChange(
                      buildChangeset(fieldValue, operatorArg as Operator),
                    );
                  }}
                />
              </CodeInput>
            );
          }}
        />
      </div>
      {onRemove && (
        <div className="basis-[18px]" tabIndex={-1}>
          <Icon
            color="text-gray-500"
            icon={faTrashCan}
            size="2xs"
            onClick={onRemove}
          />
        </div>
      )}
    </div>
  );
};
